import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ClientService {
  constructor(private http: HttpClient) {}

  show(id) {
    return this.http.get<any>(`${environment.apiUrl}/client/show/${id}`);
  }

  getClients() {
    return this.http.get<any>(`${environment.apiUrl}/client`);
  }

  store(client) {
    return this.http.post<any>(`${environment.apiUrl}/client`, client);
  }

  getContacts(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/project/contacts/${clientId}`);
  }

  getClientBuyers(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/project/client-buyers/${clientId}`);
  }

  storeContact(contact) {
    return this.http.post<any>(`${environment.apiUrl}/client/contact`, contact);
  }

  getContact(contactId) {
    return this.http.get<any>(`${environment.apiUrl}/project/contact/${contactId}`);
  }

  updateContact(param, contactId) {
    return this.http.put<any>(`${environment.apiUrl}/project/contact/${contactId}`, param);
  }

  update(client) {
    return this.http.put<any>(`${environment.apiUrl}/client/${client.id}`, client);
  }

  delete(id) {
    return this.http.delete<any>(`${environment.apiUrl}/client/${id}`);
  }

  deleteContactPerson(id) {
    return this.http.delete<any>(`${environment.apiUrl}/client/contact/${id}`);
  }

  deleteClientLocation(id) {
    return this.http.delete<any>(`${environment.apiUrl}/client/location/${id}`);
  }

  // JS-1871 (Self Service 4b): create/update a client-scoped row in the shared `locations` table.
  storeClientLocation(location) {
    return this.http.post<any>(`${environment.apiUrl}/client/location`, location);
  }
  
  deleteClientDepartment(id) {
    return this.http.delete<any>(`${environment.apiUrl}/client/department/${id}`);
  }

  storeDepartment(department) {
    return this.http.post<any>(`${environment.apiUrl}/client/department`, department);
  }

  deleteClientLogo(id) {
    return this.http.delete<any>(`${environment.apiUrl}/client/logo/${id}`);
  }

  getClientLogoPath(id) {
    return this.http.get<any>(`${environment.apiUrl}/client/logo/${id}`);
  }

  getClientStaff(){
    return this.http.get<any>(`${environment.apiUrl}/client/staff`);
  }

  addClientRole(model){
    return this.http.post<any>(`${environment.apiUrl}/client/roles`, model)
  }
  editClientRole(id:any,model){
    return this.http.put<any>(`${environment.apiUrl}/client/roles/${id}`, model)
  }

  getClientRolesForAgency(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/client/roles/${clientId}`)
  }

  getClientRoles(){
    return this.http.get<any>(`${environment.apiUrl}/client/roles`)
  }

  getClientRole(id){
    return this.http.get<any>(`${environment.apiUrl}/client/role/${id}`);
  }

  deleteClientRole(roleId){
    return this.http.delete<any>(`${environment.apiUrl}/client/roles/${roleId}`)
  }

  roleTemplates(){
    return this.http.get<any>(`${environment.apiUrl}/agency/template?type=role`)
  }

  inviteClientBuyer(params){
    return this.http.post<any>(`${environment.apiUrl}/client/invite-client-buyer`, params)
  }

  inviteTimestampSupervisor(params){
    return this.http.post<any>(`${environment.apiUrl}/client/invite-timestamp-supervisor`, params)
  }

  getClientOrderDefaultLocationId(id){
    return this.http.get<any>(`${environment.apiUrl}/client/order-default-location/${id}`)
  }

  storeClientOrderDefaultLocation(id, params){
    return this.http.post<any>(`${environment.apiUrl}/client/order-default-location/${id}`, params)
  }

  getTariffGroups(id, params: any = {}) {
    return this.http.get<any>(`${environment.apiUrl}/client/get-tariff-groups/${id}`, { params });
  }

  addTariffGroup(model) {
    return this.http.post<any>(`${environment.apiUrl}/client/add-tariff-group`, model)
  }

  editTariffGroup(model) {
    return this.http.post<any>(`${environment.apiUrl}/client/edit-tariff-group`, model)
  }

  deleteTariffGroup(id) {
    return this.http.delete<any>(`${environment.apiUrl}/client/delete-tariff-group/${id}`)
  }

  getDepartmentsByProjectId(id) {
    return this.http.get<any>(`${environment.apiUrl}/client/departments-by-project/${id}`)
  }

  getDepartmentsByClientId(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/client/departments-by-client/${clientId}`)
  }

  getDepartmentReports(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/department-reports`, params)
  }

  getMaleAndFemaleReports(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/male-and-female-reports`, params)
  }

  getAgeSplitReports(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/age-split-reports`, params)
  }

  getWorkTimeReports(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/work-time-reports`, params)
  }

  getMissionsDataReports(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/mission-data-reports`, params)
  }

  getClientUsers(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/client/users/${clientId}`)
  }

  updateClientUserDepartments(userId, params) {
    return this.http.post<any>(`${environment.apiUrl}/client/user-departments/${userId}`, params)
  }

  getClientDepartments() {
    return this.http.get<any>(`${environment.apiUrl}/client/departments`)
  }

  getClientRolesByDepartments(selectedDepartments) {
    let queryString = '';
    if (selectedDepartments) queryString += '?selectedDepartments=' + selectedDepartments;

    return this.http.get<any>(`${environment.apiUrl}/client/roles-by-departments${queryString}`)
  }

  getUserDashboardWidgets(windowWidth) {
    return this.http.get<any>(`${environment.apiUrl}/client/user-dashboard-widgets/${windowWidth}`)
  }

  updateUserDashboardWidgets(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/user-dashboard-widgets`, params)
  }

  updateUserBlacklistStatus(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/user-blacklist`, params);
  }  

  getBlockedEmployees(id) {
    return this.http.get<any>(`${environment.apiUrl}/client/blocked-employees/${id}`);
  }  

  getWishlistedEmployees(id) {
    return this.http.get<any>(`${environment.apiUrl}/client/wishlisted-employees/${id}`);
  }  

  // JS-1920 (2026-10-21): optional clientId pipes through to the backend as ?client_id= so
  // agency-side callers (e.g. orders step 2 on a specific client's order) get the per-client
  // overrides via the resolver instead of the agency-wide list. Without the arg, backend
  // keeps the pre-ticket behavior (agency-wide for agency users, user->client_id for client users).
  getClothingRequirements(clientId?: number) {
    const query = clientId ? `?client_id=${clientId}` : '';
    return this.http.get<any>(`${environment.apiUrl}/client/agency-clothing-requirements${query}`);
  }

  // Cost Center Methods
  getClientCostCenters(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/client/cost-centers/${clientId}`);
  }

  getClientCostCenterType(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/client/cost-center-type/${clientId}`);
  }

  createClientCostCenter(params) {
    return this.http.post<any>(`${environment.apiUrl}/client/cost-centers`, params);
  }

  updateClientCostCenter(id, params) {
    return this.http.put<any>(`${environment.apiUrl}/client/cost-centers/${id}`, params);
  }

  deleteClientCostCenter(id) {
    return this.http.delete<any>(`${environment.apiUrl}/client/cost-centers/${id}`);
  }

  getPoolEmployees(clientId) {
    return this.http.get<any>(`${environment.apiUrl}/client/pool/${clientId}`);
  }

  getPoolCandidates(clientId, q = '') {
    const query = q ? `?q=${encodeURIComponent(q)}` : '';
    return this.http.get<any>(`${environment.apiUrl}/client/pool/${clientId}/candidates${query}`);
  }

  addPoolEmployees(clientId, employeeIds: number[]) {
    return this.http.post<any>(`${environment.apiUrl}/client/pool/${clientId}`, { employee_ids: employeeIds });
  }

  removePoolEmployee(clientId, employeeId) {
    return this.http.delete<any>(`${environment.apiUrl}/client/pool/${clientId}/${employeeId}`);
  }

  getAgencyClientsForPool() {
    return this.http.get<any>(`${environment.apiUrl}/client/agency-clients-for-pool`);
  }

  getEmployeeClientPool(employeeId) {
    return this.http.get<any>(`${environment.apiUrl}/client/employee-pool/${employeeId}`);
  }

  syncEmployeeClientPool(employeeId, clientIds: number[]) {
    return this.http.put<any>(`${environment.apiUrl}/client/employee-pool/${employeeId}`, { client_ids: clientIds });
  }

  // JS-1871 — agency-side view of employee's client-list memberships across pool clients.
  // JS-1920 follow-up (2026-11-02): optional clientId scopes GET to a single client's
  // groups + the employee's memberships for that client, so the per-client picker on the
  // Client Pool accordion can render a client-specific list without pulling the whole set.
  getEmployeeAgencyClientLists(employeeId, clientId: number | null = null) {
    const q = clientId ? `?client_id=${clientId}` : '';
    return this.http.get<any>(`${environment.apiUrl}/client/employee-agency-client-lists/${employeeId}${q}`);
  }

  // JS-1920 follow-up (2026-11-02): optional clientId scopes the PUT diff to one client's
  // groups — unticking a list for client A no longer wipes membership rows for clients B/C.
  syncEmployeeAgencyClientLists(employeeId, groupIds: number[], clientId: number | null = null) {
    const body: any = { group_ids: groupIds };
    if (clientId) {
      body.client_id = clientId;
    }
    return this.http.put<any>(`${environment.apiUrl}/client/employee-agency-client-lists/${employeeId}`, body);
  }

  // JS-1920 (2026-10-19): per-client Portal Settings (FTE, mandays, NOH). Agency-side only;
  // backend returns 403 for users without 'Manage Clients' or outside the client's agency.
  getPortalSettings(clientId: number) {
    return this.http.get<any>(`${environment.apiUrl}/client/${clientId}/portal-settings`);
  }

  // JS-1920 (2026-10-20): per-client Clothing Requirements CRUD. Mirror of the agency
  // endpoints under /agency/agency-clothing-requirements; the frontend component branches
  // on an injected [clientId] to pick the right service family.
  getClientClothingRequirements(clientId: number) {
    return this.http.get<any>(`${environment.apiUrl}/client/${clientId}/clothing-requirements`);
  }

  saveClientClothingRequirement(clientId: number, payload: { type: string; title: { en: string; de: string; fr: string; it: string } }) {
    return this.http.post<any>(`${environment.apiUrl}/client/${clientId}/clothing-requirements`, payload);
  }

  updateClientClothingRequirement(clientId: number, id: number, payload: { title: { en: string; de: string; fr: string; it: string } }) {
    return this.http.put<any>(`${environment.apiUrl}/client/${clientId}/clothing-requirements/${id}`, payload);
  }

  deleteClientClothingRequirement(clientId: number, id: number) {
    return this.http.delete<any>(`${environment.apiUrl}/client/${clientId}/clothing-requirements/${id}`);
  }

  savePortalSettings(clientId: number, payload: {
    fte?: number | null;
    mandays?: number | null;
    noh?: {
      working_hours?: string | null;
      noh_functionality?: 0 | 1;
      project_type_id?: number | null;
      agency_contact_id?: number | null;
      email_template_id?: number | null;
      buffer_time?: number | null;
      auto_assginment_after_x_mins?: number | null;
      auto_accept_mission_requests?: 0 | 1;
    } | null;
  }) {
    return this.http.put<any>(`${environment.apiUrl}/client/${clientId}/portal-settings`, payload);
  }
}
